import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { useApi, formatPrice } from '../lib/api';

type Order = {
  order_id: string;
  status: string;
  total_cents: number;
  quantity: number;
  created_at: number;
  tickets_transferred_at: number | null;
  section: string;
  row: string;
  seats_json: string;
  opponent: string;
  game_date: string;
};

export default function OrdersPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const [orders, setOrders] = useState<Order[]>([]);
  const justPaid = params.get('status') === 'success' && orderId;

  async function refresh() {
    const { orders } = await api<{ orders: Order[] }>('/orders/mine');
    setOrders(orders);
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  async function confirm(id: string) {
    await api(`/orders/${id}/confirm`, { method: 'POST' });
    await refresh();
  }
  async function dispute(id: string) {
    const reason = window.prompt('Describe the problem (min 5 chars):');
    if (!reason || reason.length < 5) return;
    await api(`/orders/${id}/dispute`, { method: 'POST', body: JSON.stringify({ reason }) });
    await refresh();
  }

  return (
    <section>
      <h2>My orders</h2>
      <SignedOut>
        <SignInButton mode="modal" />
      </SignedOut>
      <SignedIn>
        {justPaid && (
          <div className="card" style={{ borderColor: '#0b2265', marginBottom: '1rem' }}>
            <h3>Payment received</h3>
            <p>
              Your payment is held securely by NYGTIX in escrow. The seller has been notified to
              transfer your tickets within 24 hours. We release the funds once you confirm receipt
              (or automatically 48 hours after transfer).
            </p>
          </div>
        )}
        {orders.length === 0 && <p className="muted">No orders yet.</p>}
        <div className="grid cols-3">
          {orders.map((o) => {
            const seats = JSON.parse(o.seats_json) as string[];
            return (
              <div key={o.order_id} className="card">
                <p>
                  <strong>{o.opponent}</strong> — {o.game_date}
                </p>
                <p className="muted">
                  Sec {o.section} Row {o.row} · Seats {seats.join(', ')}
                </p>
                <p className="price">{formatPrice(o.total_cents)}</p>
                <p>
                  <span className="badge">{o.status.replace(/_/g, ' ')}</span>
                </p>
                {o.status === 'tickets_transferred' && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => confirm(o.order_id)}>Confirm receipt</button>
                    <button className="btn-secondary" onClick={() => dispute(o.order_id)}>
                      Dispute
                    </button>
                  </div>
                )}
                {o.status === 'paid_escrow' && (
                  <>
                    <p className="muted">Waiting on the seller to transfer your tickets.</p>
                    <button className="btn-secondary" onClick={() => dispute(o.order_id)}>
                      Report a problem
                    </button>
                  </>
                )}
                {o.status === 'disputed' && (
                  <p className="muted">Your dispute is under review by NYGTIX.</p>
                )}
                {(o.status === 'refunded' || o.status === 'cancelled') && (
                  <p className="muted">This order was {o.status}.</p>
                )}
                {o.status === 'completed' && <p className="muted">Funds released to seller.</p>}
              </div>
            );
          })}
        </div>
      </SignedIn>
    </section>
  );
}
